import React, { useEffect, useState } from 'react';
import { crearRun } from './api';
import { formularioDefault } from './formulario';
import type { BodyCrearRun, ConfigAnalisis, ConfigFormulario, RunCreada } from './tipos';
import { Aviso, btnPrimario, btnSecundario, inputCls } from './ui';

const MAX_PROMPT = 12000;
const MAX_IDS = 500;

/**
 * Formulario para lanzar una corrida sobre los casos seleccionados en Casos. El prompt
 * viaja como `prompt_override` SOLO si difiere del default del backend (comparado sin
 * espacios de borde): así la lista de corridas marca "prompt propio" solo cuando lo es.
 *
 * `inicial` prellena (guardado en localStorage o plantilla de "Reutilizar configuración")
 * y se lee UNA vez: el padre cambia la `key` para resetear. `onChange` reporta cada
 * cambio (es lo que se persiste).
 */
const FormularioCorrida: React.FC<{
  config: ConfigAnalisis;
  imagenIds: string[];
  controlIds: string[];
  onIrACorrida: (runId: string) => void;
  inicial?: ConfigFormulario | null;
  onChange?: (cfg: ConfigFormulario) => void;
}> = ({ config, imagenIds, controlIds, onIrACorrida, inicial, onChange }) => {
  const [cfg, setCfg] = useState<ConfigFormulario>(() => inicial ?? formularioDefault(config));
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creada, setCreada] = useState<RunCreada | null>(null);

  useEffect(() => {
    onChange?.(cfg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  const set = <K extends keyof ConfigFormulario>(k: K, v: ConfigFormulario[K]) => setCfg((c) => ({ ...c, [k]: v }));

  const total = imagenIds.length + controlIds.length;
  const esDefault = cfg.prompt.trim() === config.prompt_default.trim();
  const promptLargo = cfg.prompt.length > MAX_PROMPT;
  const modeloValido = config.modelos.some((m) => m.id === cfg.modelo);
  const puede = !creando && cfg.etiqueta.trim().length > 0 && total >= 1 && total <= MAX_IDS && modeloValido && !promptLargo;

  const lanzar = async () => {
    setError(null);
    setCreada(null);
    setCreando(true);
    try {
      const body: BodyCrearRun = {
        etiqueta: cfg.etiqueta.trim(),
        modelo: cfg.modelo,
        imagen_ids: imagenIds,
        fotos_producto: cfg.fotos,
        ...(cfg.effort ? { effort: cfg.effort } : {}),
        ...(controlIds.length > 0 ? { control_ids: controlIds } : {}),
        ...(esDefault || cfg.prompt.trim() === '' ? {} : { prompt_override: cfg.prompt }),
      };
      const r = await crearRun(body);
      setCreada(r);
    } catch (e) {
      console.error('[analisis] crear corrida falló:', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={cfg.etiqueta}
          onChange={(e) => set('etiqueta', e.target.value)}
          placeholder='Etiqueta (ej. "opus5-prompt-v1")'
          className={`${inputCls} w-56`}
        />
        <label className="flex items-center gap-2 text-xs text-gray-500">
          Modelo
          <select value={cfg.modelo} onChange={(e) => set('modelo', e.target.value)} className={inputCls}>
            {!modeloValido && <option value={cfg.modelo}>{cfg.modelo} (no está en el catálogo)</option>}
            {config.modelos.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label} ({m.proveedor})
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-500">
          Effort
          <select value={cfg.effort} onChange={(e) => set('effort', e.target.value)} className={inputCls}>
            {config.efforts.map((ef) => (
              <option key={ef} value={ef}>
                {ef}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-500">
          Fotos de producto
          <select value={cfg.fotos} onChange={(e) => set('fotos', e.target.value as 'opt' | 'full')} className={inputCls}>
            <option value="opt">opt (livianas)</option>
            <option value="full">full (originales)</option>
          </select>
        </label>
      </div>

      <div>
        <div className="mb-1 flex items-center gap-3 text-xs text-gray-500">
          <span>
            Prompt del análisis {esDefault ? '(default del backend)' : <b className="text-brand-black">(propio — viaja como prompt_override)</b>}
          </span>
          <span className={promptLargo ? 'font-semibold text-red-600' : ''}>
            {cfg.prompt.length.toLocaleString('es-AR')} / {MAX_PROMPT.toLocaleString('es-AR')}
          </span>
          {!esDefault && (
            <button type="button" onClick={() => set('prompt', config.prompt_default)} className="ml-auto text-blue-600 underline">
              Restaurar default
            </button>
          )}
        </div>
        <textarea
          value={cfg.prompt}
          onChange={(e) => set('prompt', e.target.value)}
          rows={8}
          spellCheck={false}
          className="w-full rounded-lg border border-gray-300 p-3 font-mono text-[11px] leading-snug text-brand-black"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => void lanzar()} disabled={!puede} className={btnPrimario}>
          {creando ? 'Encolando…' : `Analizar ${total} ${total === 1 ? 'caso' : 'casos'}`}
        </button>
        <span className="text-xs text-gray-500">
          {imagenIds.length} editadas + {controlIds.length} control
          {total > MAX_IDS && <b className="text-red-600"> · máximo {MAX_IDS}</b>}
          {total === 0 && ' · seleccioná al menos un caso'}
          {!modeloValido && <b className="text-red-600"> · elegí un modelo del catálogo</b>}
        </span>
        {creada && (
          <span className="flex items-center gap-2 text-xs text-green-700">
            Corrida encolada: <code className="rounded bg-gray-100 px-1 text-brand-black">{creada.run_id}</code> · {creada.total} casos
            <button type="button" onClick={() => onIrACorrida(creada.run_id)} className={`${btnSecundario} py-1.5 px-3 text-xs`}>
              Ver corrida →
            </button>
          </span>
        )}
      </div>
      <Aviso error={error} />
    </div>
  );
};

export default FormularioCorrida;
